import React, { useEffect, useState } from "react";
import { Form, Container, Row, Col, Card } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  addDayHotel,
  editDayHotel,
  getDayPlan,
  getoptions,
} from "../../store/slices/package";
import { getPlace } from "../../store/slices/place";
import { gettravelHotel } from "../../store/slices/travelhotel";
import { getroomType } from "../../store/slices/roomtype";
import { toast } from "react-toastify";
import Select from "react-select";

export default function HotelStep({
  packageId,
  hotels,
  setHotels,
  nextStep,
  prevStep,
}) {
  const dispatch = useDispatch();

  const [dayPlans, setDayPlans] = useState([]);
  const [options, setOptions] = useState([]);
  const [roomTypesMap, setRoomTypesMap] = useState({});
  const [hotelsMap, setHotelsMap] = useState({});
  const [places, setPlaces] = useState([]);

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!packageId) return;

    const fetchData = async () => {
      try {
        const item = { packageId };

        const dpRes = await dispatch(getDayPlan(item)).unwrap();
        const optRes = await dispatch(getoptions(item)).unwrap();
        // const hotelRes = await dispatch(gettravelHotel()).unwrap();
        const placeRes = await dispatch(getPlace()).unwrap();

        const dp = dpRes?.data || [];
        const opt = optRes?.data || [];

        setDayPlans(dp);
        setOptions(opt);
        setPlaces(placeRes?.places || placeRes?.data || []);
      } catch (err) {
        toast.error("Failed to load hotel data");
      }
    };

    fetchData();
  }, [packageId, dispatch]);

  useEffect(() => {
    const loadEditHotels = async () => {
      try {
        // Normalize existing edit data first
        const normalizedHotels = hotels.map((config) => ({
          ...config,

          // Convert populated objects to IDs
          hotel:
            typeof config.hotel === "object"
              ? config.hotel?._id
              : config.hotel,

          roomType:
            typeof config.roomType === "object"
              ? config.roomType?._id
              : config.roomType,

          packageOption:
            typeof config.packageOption === "object"
              ? config.packageOption?._id
              : config.packageOption,

          dayPlan:
            typeof config.dayPlan === "object"
              ? config.dayPlan?._id
              : config.dayPlan,

          // Preserve saved addons
          addons: Array.isArray(config.addons)
            ? config.addons
            : [],
        }));

        // Update only if normalization actually changed something
        setHotels(normalizedHotels);

        for (let i = 0; i < normalizedHotels.length; i++) {
          const config = normalizedHotels[i];

          // Load hotels
          if (config.place) {
            const hotelRes = await dispatch(
              gettravelHotel({
                location: config.place,
              })
            ).unwrap();

            setHotelsMap((prev) => ({
              ...prev,
              [i]: hotelRes?.hotels || [],
            }));
          }

          // Load room types
          if (config.hotel) {
            const roomRes = await dispatch(
              getroomType({
                hotelId: config.hotel,
              })
            ).unwrap();

            setRoomTypesMap((prev) => ({
              ...prev,
              [i]: roomRes?.rooms || [],
            }));
          }
        }
      } catch (err) {
        console.error("Failed to load edit hotels:", err);
        toast.error("Failed to load hotel data");
      }
    };

    if (hotels.length > 0) {
      loadEditHotels();
    }
  }, [hotels.length]);

  useEffect(() => {
    if (!options.length || !dayPlans.length) return;

    setHotels((prevHotels) => {
      if (prevHotels.length > 0) return prevHotels;

      let generated = [];

      const stayDays = dayPlans.slice(0, dayPlans.length - 1);

      options.forEach((option) => {
        stayDays.forEach((day) => {
          generated.push({
            _id: null,
            packageOption: option._id,
            dayPlan: day._id,
            place: "",
            hotel: "",
            roomType: "",
            nights: 1,
            roomPrice: 0,
            extraBedPrice: 0,

            rooms: 1,
            extraBeds: 0,
            meals: {
              breakfast: false,
              lunch: false,
              dinner: false,
            },
          });
        });
      });

      return generated;
    });
  }, [options, dayPlans]);

  const handleChange = async (index, field, value) => {
    const updated = [...hotels];
    const newErrors = [...errors];

    if (!newErrors[index]) newErrors[index] = {};

    if (field === "place") {
      updated[index].place = value;
      updated[index].hotel = "";
      updated[index].roomType = "";
      updated[index].roomPrice = 0;
      updated[index].extraBedPrice = 0;

      setRoomTypesMap((prev) => ({
        ...prev,
        [index]: [],
      }));

      if (value) {
        try {
          const hotelRes = await dispatch(
            gettravelHotel({ location: value }),
          ).unwrap();

          setHotelsMap((prev) => ({
            ...prev,
            [index]: hotelRes?.hotels || [],
          }));
        } catch (err) {
          toast.error("Failed to load hotels");
        }
      }

      setHotels(updated);
      return;
    }
    if (field === "place") {
      if (!value) {
        newErrors[index].place = "Please select place";
      } else {
        newErrors[index].place = "";
      }
    }

    if (field === "hotel") {
      if (!value) {
        newErrors[index].hotel = "Please select hotel";
      } else {
        newErrors[index].hotel = "";
      }
    }

    if (field === "roomType") {
      if (!value) {
        newErrors[index].roomType = "Please select room type";
      } else {
        newErrors[index].roomType = "";
      }

      updated[index].roomType = value;

      const room = (roomTypesMap[index] || []).find(
        (r) => r._id === value
      );

      if (room) {
        updated[index].roomPrice = room.basePrice || 0;
        updated[index].extraBedPrice = room.extraBedPrice || 0;

        // Don't clear saved addons unnecessarily
        if (!Array.isArray(updated[index].addons)) {
          updated[index].addons = [];
        }
      } else {
        updated[index].addons = [];
      }

      setHotels(updated);
      setErrors(newErrors);
      return;
    }

    if (field === "nights") {
      if (!value) {
        newErrors[index].nights = "Please enter nights";
      } else if (!/^\d+$/.test(value)) {
        newErrors[index].nights = "Only numbers allowed";
        return; // block letters
      } else if (Number(value) > 20) {
        newErrors[index].nights = "Maximum 20 nights allowed";
        return;
      } else {
        newErrors[index].nights = "";
      }
    }

    if (field === "rooms") {
      if (!value) {
        newErrors[index].rooms = "Please enter rooms";
      } else if (!/^\d+$/.test(value)) {
        newErrors[index].rooms = "Only numbers allowed";
        return;
      } else if (Number(value) > 5) {
        newErrors[index].rooms = "Maximum 5 rooms allowed";
        return;
      } else {
        newErrors[index].rooms = "";
      }
    }

    if (field === "extraBeds") {
      if (!value) {
        newErrors[index].extraBeds = "Please enter extra beds";
      } else if (!/^\d+$/.test(value)) {
        newErrors[index].extraBeds = "Only numbers allowed";
        return;
      } else if (Number(value) > 5) {
        newErrors[index].extraBeds = "Maximum 5 extra beds allowed";
        return;
      } else {
        newErrors[index].extraBeds = "";
      }
    }

    updated[index][field] = value;

    if (field === "roomType") {
      const room = (roomTypesMap[index] || []).find(
        (r) => r._id === value
      );

      if (room) {
        updated[index].roomPrice = room.basePrice || 0;
        updated[index].extraBedPrice = room.extraBedPrice || 0;
      }
    }

    setErrors(newErrors);
    setHotels(updated);

    if (field === "hotel" && value) {
      try {
        const roomRes = await dispatch(
          getroomType({ hotelId: value })
        ).unwrap();

        setRoomTypesMap((prev) => ({
          ...prev,
          [index]: roomRes?.rooms || [],
        }));
      } catch (err) {
        toast.error("Failed to load room types");
      }
    }
  };

  const validateHotelSubmit = () => {
    let isValid = true;

    const newErrors = hotels.map((config) => {
      const errorObj = {};

      if (!config.place) {
        errorObj.place = "Please select place";
        isValid = false;
      }

      if (config.hotel && !config.roomType) {
        errorObj.roomType = "Please select room type";
        isValid = false;
      }
      if (config.hotel && (!config.roomPrice || config.roomPrice <= 0)) {
        errorObj.roomPrice = "Please enter room price";
        isValid = false;
      }

      if (!config.nights || config.nights < 1 || config.nights > 21) {
        errorObj.nights = "Nights must be 1 - 30";
        isValid = false;
      }

      if (!config.rooms || config.rooms < 1 || config.rooms > 6) {
        errorObj.rooms = "Rooms must be 1 - 20";
        isValid = false;
      }

      if (config.extraBeds < 0 || config.extraBeds > 6) {
        errorObj.extraBeds = "Extra beds must be 0 - 10";
        isValid = false;
      }

      return errorObj;
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleMealChange = (index, meal) => {
    const updated = [...hotels];
    const updatedErrors = [...errors];

    if (!updatedErrors[index]) updatedErrors[index] = {};

    updated[index] = {
      ...updated[index],
      meals: {
        ...updated[index].meals,
        [meal]: !updated[index].meals?.[meal],
      },
    };

    const { breakfast, lunch, dinner } = updated[index].meals;

    if (!breakfast && !lunch && !dinner) {
      updatedErrors[index].meals = "Please select at least one meal";
    } else {
      updatedErrors[index].meals = "";
    }

    setHotels(updated);
    setErrors(updatedErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasAnyHotelSelected = hotels.some((h) => h.hotel);
    if (hasAnyHotelSelected && !validateHotelSubmit()) return;

    try {
      const filteredHotels = hotels.filter((h) => h.hotel);

      for (const config of filteredHotels) {
        const payload = {
          ...config,
          packageId,
          nights: Number(config.nights),
          rooms: Number(config.rooms),
          extraBeds: Number(config.extraBeds),
        };
        console.log("Payload", payload);
        if (config._id) {
          await dispatch(
            editDayHotel({
              id: config._id,
              ...payload,
            }),
          ).unwrap();
        } else {
          const res = await dispatch(addDayHotel(payload)).unwrap();
          config._id = res?._id || res?.data?._id || res?.hotel?._id || null;
        }
      }

      //  Always allow next step
      nextStep();
    } catch (err) {
      console.log(err);
      console.log(err?.response);
      console.log(err?.response?.data);
      toast.error(err?.response?.data?.message || "Failed to save hotels");
    }
  };

  const hotelsByOption = options.map((option) => ({
    option,
    configs: hotels.filter((h) => h.packageOption === option._id),
  }));

  return (
    <Container>
      <Card className="p-4 shadow-sm border-0 mt-2">
        <Row>
          {hotelsByOption.map(({ option, configs }) => (
            <Col md={6} key={option._id}>
              <Card className="p-3 mb-3 shadow-sm">
                <h5 className="text-center mb-3">{option.name}</h5>

                {configs.map((config) => {
                  const index = hotels.findIndex(
                    (h) =>
                      h.packageOption === config.packageOption &&
                      h.dayPlan === config.dayPlan,
                  );

                  const day = dayPlans.find((d) => d._id === config.dayPlan);

                  return (
                    <Card key={index} className="mb-3 p-3 border">
                      <h6>Day {day?.dayNumber}</h6>

                      <Row className="g-3">
                        {/* Place */}
                        <Col md={12}>
                          <Form.Label>Place</Form.Label>
                          <Select
                            placeholder="Select Place"
                            value={
                              places
                                .map((p) => ({
                                  value: p._id,
                                  label: p.name,
                                }))
                                .find((o) => o.value === config.place) || null
                            }
                            onChange={(selected) =>
                              handleChange(index, "place", selected?.value || "")
                            }
                            options={places.map((p) => ({
                              value: p._id,
                              label: p.name,
                            }))}
                          />
                        </Col>

                        {/* Hotel */}
                        <Col md={12}>
                          <Form.Label>Hotel</Form.Label>
                          <Select
                            placeholder="Select Hotel"
                            isDisabled={!config.place}
                            value={
                              (hotelsMap[index] || [])
                                .map((h) => ({
                                  value: h._id,
                                  label: h.name,
                                }))
                                .find((o) => o.value === config.hotel) || null
                            }
                            onChange={(selected) =>
                              handleChange(index, "hotel", selected?.value || "")
                            }
                            options={(hotelsMap[index] || []).map((h) => ({
                              value: h._id,
                              label: h.name,
                            }))}
                          />
                        </Col>

                        {/* Room Type */}
                        <Col md={6}>
                          <Form.Label>Room Type</Form.Label>
                          <Form.Select
                            value={config.roomType}
                            disabled={!config.hotel}
                            onChange={(e) =>
                              handleChange(index, "roomType", e.target.value)
                            }
                          >
                            <option value="">Select Room</option>
                            {(roomTypesMap[index] || []).map((rt) => (
                              <option key={rt._id} value={rt._id}>
                                {rt.name} (₹{rt.basePrice})
                              </option>
                            ))}
                          </Form.Select>
                        </Col>

                        <Col md={6}>
                          <Form.Label>Room Price</Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={config.roomPrice || ""}
                            onChange={(e) =>
                              handleChange(index, "roomPrice", e.target.value)
                            }
                          />
                          {errors[index]?.roomPrice && (
                            <div style={{ color: "red", fontSize: "12px" }}>
                              {errors[index].roomPrice}
                            </div>
                          )}
                        </Col>

                        <Col md={4}>
                          <Form.Label>Extra Bed Price</Form.Label>
                          <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={config.extraBedPrice || ""}
                            onChange={(e) =>
                              handleChange(index, "extraBedPrice", e.target.value)
                            }
                          />
                        </Col>




                        {/* Meals */}
                        <Col md={8}>
                          <Form.Label>Meals</Form.Label>

                          <div className="d-flex gap-3">
                            <Form.Check
                              type="checkbox"
                              label="Breakfast"
                              checked={config.meals?.breakfast || false}
                              onChange={() =>
                                handleMealChange(index, "breakfast")
                              }
                            />

                            <Form.Check
                              type="checkbox"
                              label="Lunch"
                              checked={config.meals?.lunch || false}
                              onChange={() => handleMealChange(index, "lunch")}
                            />

                            <Form.Check
                              type="checkbox"
                              label="Dinner"
                              checked={config.meals?.dinner || false}
                              onChange={() => handleMealChange(index, "dinner")}
                            />
                          </div>

                          {errors[index]?.meals && (
                            <div style={{ color: "red", fontSize: "12px" }}>
                              {errors[index].meals}
                            </div>
                          )}
                        </Col>
                        <Col md={12}>
                          <Form.Label>Hotel Add-ons</Form.Label>

                          {(() => {
                            const selectedRoom = (roomTypesMap[index] || []).find(
                              (r) => String(r._id) === String(config.roomType)
                            );

                            // Available addons from getroomType response
                            const addons = selectedRoom?.hotel?.addons || [];

                            if (!config.roomType) {
                              return (
                                <div className="text-muted">
                                  Please select a room type first
                                </div>
                              );
                            }

                            if (addons.length === 0) {
                              return (
                                <div className="text-muted">
                                  No add-ons available for this hotel
                                </div>
                              );
                            }

                            return (
                              <div className="d-flex flex-column gap-2">
                                {addons.map((addon, addonIndex) => {

                                  // Check saved addons
                                  const selected = (config.addons || []).some(
                                    (item) => {
                                      const savedName = String(item?.name || "").trim();
                                      const availableName = String(addon?.name || "").trim();

                                      return (
                                        (item?.addonId &&
                                          addon?._id &&
                                          String(item.addonId) === String(addon._id)) ||
                                        (savedName && availableName && savedName === availableName)
                                      );
                                    }
                                  );

                                  return (
                                    <Form.Check
                                      key={addon._id || addonIndex}
                                      type="checkbox"
                                      label={`${addon.name} - ₹${addon.price || 0}`}
                                      checked={selected}
                                      onChange={(e) => {
                                        const updated = [...hotels];

                                        const currentAddons =
                                          updated[index].addons || [];

                                        if (e.target.checked) {
                                          const alreadySelected = currentAddons.some(
                                            (item) =>
                                              (
                                                item.addonId &&
                                                addon._id &&
                                                String(item.addonId) === String(addon._id)
                                              ) ||
                                              item.name?.trim() === addon.name?.trim()
                                          );

                                          if (!alreadySelected) {
                                            updated[index].addons = [
                                              ...currentAddons,
                                              {
                                                addonId: addon._id || null,
                                                name: addon.name,
                                                type: addon.type,
                                                price: Number(addon.price || 0),
                                                quantity: 1,
                                                isIncluded: true,
                                              },
                                            ];
                                          }
                                        } else {
                                          updated[index].addons = currentAddons.filter(
                                            (item) =>
                                              item.name?.trim() !== addon.name?.trim()
                                          );
                                        }

                                        setHotels(updated);
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Card>
            </Col>
          ))}
        </Row>

        <div className="d-flex justify-content-between mt-4 mb-5">
          <button className="cancel-btn" onClick={prevStep}>
            Previous
          </button>
          <button className="cancel-btn" onClick={nextStep}>
            Skip
          </button>

          <button className="submit-btn" onClick={handleSubmit}>
            Next
          </button>
        </div>
      </Card>
    </Container>
  );
}